import { useAppSelector } from "../../store/hooks";
import { selectIsAdmin } from "../../store/authSelectors";
import {
  useGetQuery,
  useAddMutation,
  useUpdateMutation,
  useRemoveMutation,
} from "../../store/slices/apiSlice";
import { useToast } from "./use-toast";
import { getErrorMessage } from "./use-getError";
import {
  ProjectRole,
  ProjectInstance,
  CreateProjectInstanceData,
  EditProjectInstanceData,
  InstanceClient,
  InstanceClientData,
  RoleAssignment,
} from "../../store/types";

/**
 * API единого входа: каталог ролей продукта, инстансы, их клиенты и назначения ролей.
 * Все ручки только для SystemAdmin. Контракт — docs/sso-developer-guide.md в ComradeMajor.
 */

type MutationResult = { data?: any; error?: any };

/** Общая обработка ответа: тост об успехе или текст ошибки сервера (ProblemDetails.title). */
const useMutationRunner = () => {
  const { toastRef, showSuccess, showError } = useToast();

  const run = async <T = any>(
    operation: string,
    action: () => Promise<MutationResult>,
    successText?: string
  ): Promise<{ ok: boolean; data?: T }> => {
    try {
      const result = await action();

      if (result.error) {
        showError(
          "Ошибка",
          `Не удалось ${operation}: ${getErrorMessage(result.error)}`
        );
        return { ok: false };
      }

      if (successText) showSuccess("Успешно", successText);
      return { ok: true, data: result.data as T };
    } catch (error) {
      showError("Ошибка", `Не удалось ${operation}: ${error}`);
      return { ok: false };
    }
  };

  return { run, toastRef };
};

const enc = (value: string) => encodeURIComponent(value);

/** Каталог ролей продукта и назначение роли сразу на все инстансы продукта. */
export const useProjectRolesAPI = (projectId?: string) => {
  const isAdmin = useAppSelector(selectIsAdmin);
  const projectsEP = useAppSelector((state) => state.endpoints.projects);
  const rolesEP = projectId ? `${projectsEP}/${enc(projectId)}/roles` : "";

  const { run, toastRef } = useMutationRunner();
  const [addItem] = useAddMutation();
  const [editItem] = useUpdateMutation();
  const [deleteItem] = useRemoveMutation();

  const { data, refetch } = useGetQuery(rolesEP, {
    refetchOnMountOrArgChange: true,
    skip: !rolesEP || !isAdmin,
  });

  const roles: ProjectRole[] = data ?? [];

  const handleCreateRole = async (name: string, description: string) =>
    (
      await run("создать роль", () =>
        addItem({ endpoint: rolesEP, body: { name, description } }),
        "Роль создана"
      )
    ).ok;

  const handleUpdateRole = async (role: ProjectRole, description: string) =>
    (
      await run("изменить роль", () =>
        editItem({ endpoint: rolesEP, id: role.id, body: { description } }),
        "Роль изменена"
      )
    ).ok;

  const handleDeleteRole = async (role: ProjectRole) =>
    (
      await run("удалить роль", () =>
        deleteItem({ endpoint: rolesEP, id: role.id }),
        "Роль удалена"
      )
    ).ok;

  /** Роль пользователю на всех существующих инстансах продукта. Возвращает число новых назначений. */
  const handleGrantOnAllInstances = async (roleId: string, userId: string) => {
    const result = await run<{ count: number }>("назначить роль", () =>
      addItem({ endpoint: `${rolesEP}/${roleId}/assignments`, body: { userId } })
    );
    return result.ok ? result.data?.count ?? 0 : null;
  };

  /** Снять роль с пользователя на всех инстансах продукта. Возвращает число снятых назначений. */
  const handleRevokeOnAllInstances = async (roleId: string, userId: string) => {
    const result = await run<{ count: number }>("снять роль", () =>
      deleteItem({ endpoint: `${rolesEP}/${roleId}/assignments`, id: userId })
    );
    return result.ok ? result.data?.count ?? 0 : null;
  };

  return {
    roles,
    refetchRoles: refetch,
    handleCreateRole,
    handleUpdateRole,
    handleDeleteRole,
    handleGrantOnAllInstances,
    handleRevokeOnAllInstances,
    toastRef,
  };
};

/** Список инстансов (фильтр по продукту и/или компании) и создание инстанса. */
export const useInstancesAPI = (filter: {
  projectId?: string;
  organizationId?: number;
}) => {
  const isAdmin = useAppSelector(selectIsAdmin);
  const instancesEP = useAppSelector((state) => state.endpoints.instances);

  const query = new URLSearchParams();
  if (filter.projectId) query.set("projectId", filter.projectId);
  if (filter.organizationId) query.set("organizationId", String(filter.organizationId));
  const listEP = `${instancesEP}?${query.toString()}`;

  const { run, toastRef } = useMutationRunner();
  const [addItem] = useAddMutation();

  const { data, refetch } = useGetQuery(listEP, {
    refetchOnMountOrArgChange: true,
    skip: !isAdmin || (!filter.projectId && !filter.organizationId),
  });

  const instances: ProjectInstance[] = data ?? [];

  const handleCreateInstance = async (instance: CreateProjectInstanceData) => {
    const result = await run<ProjectInstance>(
      "создать инстанс",
      () =>
        addItem({
          endpoint: instancesEP,
          body: { ...instance, baseUrl: instance.baseUrl || null },
        }),
      "Инстанс создан"
    );
    return result.ok ? result.data ?? null : null;
  };

  return { instances, refetchInstances: refetch, handleCreateInstance, toastRef };
};

/** Один инстанс: данные, клиент, назначения ролей. */
export const useInstanceAPI = (id?: string) => {
  const isAdmin = useAppSelector(selectIsAdmin);
  const instancesEP = useAppSelector((state) => state.endpoints.instances);
  const instanceEP = id ? `${instancesEP}/${enc(id)}` : "";

  const { run, toastRef } = useMutationRunner();
  const [addItem] = useAddMutation();
  const [editItem] = useUpdateMutation();
  const [deleteItem] = useRemoveMutation();

  const skip = !instanceEP || !isAdmin;
  const instanceQuery = useGetQuery(instanceEP, { refetchOnMountOrArgChange: true, skip });
  const clientQuery = useGetQuery(`${instanceEP}/client`, { refetchOnMountOrArgChange: true, skip });
  const assignmentsQuery = useGetQuery(`${instanceEP}/assignments`, { refetchOnMountOrArgChange: true, skip });

  const instance: ProjectInstance | null = instanceQuery.data ?? null;
  const instanceNotFound = (instanceQuery.error as any)?.status === 404;
  // 404 у клиента — клиента просто нет
  const client: InstanceClient | null = clientQuery.error ? null : clientQuery.data ?? null;
  const assignments: RoleAssignment[] = assignmentsQuery.data ?? [];

  const refetchAll = async () => {
    await Promise.all([
      instanceQuery.refetch(),
      clientQuery.refetch(),
      assignmentsQuery.refetch(),
    ]);
  };

  const handleUpdateInstance = async (data: EditProjectInstanceData) =>
    (
      await run("изменить инстанс", () =>
        editItem({ endpoint: instanceEP, body: { ...data, baseUrl: data.baseUrl || null } }),
        "Данные инстанса обновлены"
      )
    ).ok;

  const handleSetEnabled = async (isEnabled: boolean) =>
    (
      await run(isEnabled ? "включить инстанс" : "выключить инстанс", () =>
        addItem({ endpoint: `${instanceEP}/${isEnabled ? "enable" : "disable"}` }),
        isEnabled ? "Инстанс включён" : "Инстанс выключен"
      )
    ).ok;

  const handleDeleteInstance = async () =>
    (
      await run("удалить инстанс", () => deleteItem({ endpoint: instanceEP }), "Инстанс удалён")
    ).ok;

  /** Возвращает клиента вместе с секретом — показать один раз. */
  const handleCreateClient = async (data: InstanceClientData) => {
    const result = await run<InstanceClient>(
      "создать клиента",
      () => addItem({ endpoint: `${instanceEP}/client`, body: data }),
      "Клиент создан"
    );
    return result.ok ? result.data ?? null : null;
  };

  const handleUpdateClient = async (redirectUris: string[], postLogoutRedirectUris: string[]) =>
    (
      await run("изменить адреса клиента", () =>
        editItem({ endpoint: `${instanceEP}/client`, body: { redirectUris, postLogoutRedirectUris } }),
        "Адреса клиента обновлены"
      )
    ).ok;

  /** Возвращает клиента с новым секретом — показать один раз. */
  const handleRotateSecret = async () => {
    const result = await run<InstanceClient>(
      "выпустить новый секрет",
      () => addItem({ endpoint: `${instanceEP}/client/secret` }),
      "Новый секрет выпущен"
    );
    return result.ok ? result.data ?? null : null;
  };

  const handleDeleteClient = async () =>
    (
      await run("удалить клиента", () => deleteItem({ endpoint: `${instanceEP}/client` }), "Клиент удалён")
    ).ok;

  const handleGrantRole = async (userId: string, roleId: string) =>
    (
      await run("назначить роль", () =>
        addItem({ endpoint: `${instanceEP}/assignments`, body: { userId, roleId } }),
        "Роль назначена"
      )
    ).ok;

  const handleRevokeRole = async (assignment: RoleAssignment) =>
    (
      await run("снять роль", () =>
        deleteItem({ endpoint: `${instanceEP}/assignments`, id: assignment.id }),
        "Роль снята"
      )
    ).ok;

  return {
    instance,
    instanceNotFound,
    isLoading: instanceQuery.isLoading,
    client,
    assignments,
    refetchAll,
    handleUpdateInstance,
    handleSetEnabled,
    handleDeleteInstance,
    handleCreateClient,
    handleUpdateClient,
    handleRotateSecret,
    handleDeleteClient,
    handleGrantRole,
    handleRevokeRole,
    toastRef,
  };
};

/** Все глобальные роли пользователя на всех инстансах. */
export const useUserInstanceRolesAPI = (userId?: string) => {
  const isAdmin = useAppSelector(selectIsAdmin);
  const usersEP = useAppSelector((state) => state.endpoints.users);
  const ep = userId ? `${usersEP}/${userId}/instance-roles` : "";

  const { data, refetch } = useGetQuery(ep, {
    refetchOnMountOrArgChange: true,
    skip: !ep || !isAdmin,
  });

  const assignments: RoleAssignment[] = data ?? [];

  return { assignments, refetchAssignments: refetch };
};
